<script setup lang="ts">
// Tree — orthogonal node-link diagram for structural hierarchies
// (org charts, taxonomies, decision trees, program structure).
// Author returns flat `{ parent, name, value? }` rows; we share the
// `pivotHierarchyRows` helper with sunburst (the data contract is
// identical), then hand the resulting nested tree to ECharts'
// `TreeChart`.
//
// Where this differs from sunburst (the closest analogue):
//   - `value` is OPTIONAL — structural diagrams have no metric.
//   - Multi-root data is REJECTED at render time rather than silently
//     wrapped under a synthetic root (persona-review finding — silent
//     wraps mask real authoring errors; the GM sees lines going up to
//     nowhere and assumes the data is wrong).
//   - `initialTreeDepth` defaults to 1 so big trees don't dump every
//     node on first paint; matches the `sbi.viz.table` tree-layout
//     opt for one consistent "initial state" mental model.
//   - `colorBy` opts in to per-depth or per-column-value palette
//     mapping; the default is the single accent token. Sunburst's
//     per-depth palette is always-on because ring colour is the
//     visual; in a node-link tree single-accent is the safer default
//     and authors who want categorical colour ask for it explicitly.
//
// Depth cap at 16 levels mirrors sunburst — deep enough for any
// real-world hierarchy, soft-fails with a diagnostic past that so a
// runaway recursive CTE shows up as a warning rather than a
// 15-second canvas paint.

import { computed, watch } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { TreeChartSpec } from "../sbi/protocol";
import { formatValue } from "../format.ts";
import { CHART_COLORS, FONT_STACK_MONO, PALETTE } from "../theme.ts";
import { chartChrome, resolveCssVar, tooltipDefaults } from "./echarts-base";
import { useChartLayout } from "./use-chart-layout";
import {
  hierarchyMaxDepth,
  pivotHierarchyRows,
  type HierarchyTreeNode,
} from "./insight-pivot";

const TREE_MAX_DEPTH = 16;

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();
const clampPx = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, Math.round(v)));

const props = withDefaults(
  defineProps<{
    spec: TreeChartSpec;
    formats?: Record<string, string>;
  }>(),
  { formats: () => ({}) },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
  (e: "diagnostic", code: string, detail: Record<string, unknown>): void;
  (
    e: "chart-error",
    payload: {
      code: string;
      message: string;
      chartType: string;
      targetId?: string;
    },
  ): void;
}>();

// Resolved orient — `LR` by default. The schema enum already pins
// the field to `"LR" | "TB"`, so this only ever hands back one of
// those two strings.
const orient = computed<"LR" | "TB">(() => props.spec.orient ?? "LR");

// Resolved value-label policy. When the author opts into the `value`
// column, default to surfacing the value on the node so the reader
// doesn't have to hover. When `value` is omitted (structural tree),
// default to `off`. Explicit `valueLabel` always wins.
const valueLabelMode = computed<"node" | "tooltip" | "off">(() => {
  if (props.spec.valueLabel) return props.spec.valueLabel;
  return props.spec.value ? "node" : "off";
});

// Format string resolution for the value, in the same order as other
// charts: explicit `spec.format` first (per-call default), then the
// registry entry for the column name (`sbi.format(...)`).
const valueFormat = computed<string | undefined>(() => {
  const explicit = (props.spec as { format?: string }).format;
  if (explicit) return explicit;
  if (props.spec.value) return props.formats[props.spec.value];
  return undefined;
});

const pivot = computed(() => {
  const rows = props.spec.data as ReadonlyArray<Record<string, unknown>>;
  // `colorBy: "depth"` is rendered via ECharts' `levels[]` option and
  // doesn't need a per-node colour key carried through; everything
  // else (a literal column name, including the convenience alias
  // `"category"` → `"category"` column) does.
  const colorByKey =
    props.spec.colorBy && props.spec.colorBy !== "depth"
      ? props.spec.colorBy
      : undefined;
  return pivotHierarchyRows(rows, {
    parentKey: props.spec.parent,
    nameKey: props.spec.name,
    valueKey: props.spec.value,
    idKey: props.spec.idColumn,
    colorByKey,
  });
});

// Multi-root rejection. ECharts tree wants a single root at the
// series level; the persona-review consensus was that silently
// wrapping multiple roots under a hidden synthetic node masks real
// authoring errors (the GM sees an unlabeled top node and assumes
// the data is wrong; the AI author never gets the self-correction
// signal). When the pivot returns 2+ roots, we render an inline
// error and emit `chart-error` so embedders see it.
const multiRootError = computed(() => {
  const roots = pivot.value.roots;
  if (roots.length <= 1) return null;
  const sample = roots.slice(0, 3).map((r) => r.name).join(", ");
  const more = roots.length > 3 ? ` (and ${roots.length - 3} more)` : "";
  return `Tree expects a single root, got ${roots.length}: ${sample}${more}. Filter your SQL to a subtree or add an explicit parent row.`;
});

const truncatedRoot = computed<HierarchyTreeNode | null>(() => {
  const roots = pivot.value.roots;
  if (roots.length !== 1) return null;
  const root = roots[0]!;
  const max = hierarchyMaxDepth([root]);
  if (max <= TREE_MAX_DEPTH) return root;
  // Truncate: prune subtrees deeper than the cap. Root sits at depth 1
  // so a node at depth N has been clipped if its descendants would
  // reach depth N+1 once expanded.
  function clip(node: HierarchyTreeNode, depth: number): HierarchyTreeNode {
    if (!node.children || depth >= TREE_MAX_DEPTH) {
      const { children, ...rest } = node;
      void children;
      return rest;
    }
    return { ...node, children: node.children.map((c) => clip(c, depth + 1)) };
  }
  return clip(root, 1);
});

// Build a colour-key → palette-index map once per pivot when
// `colorBy: "<column>"` is set. Walking the tree and assigning a
// palette entry to each *first-seen* key keeps neighbouring nodes
// stable across re-renders (Map iteration order is insertion order).
const colorByMap = computed<Map<unknown, string> | null>(() => {
  if (!props.spec.colorBy || props.spec.colorBy === "depth") return null;
  const out = new Map<unknown, string>();
  const root = truncatedRoot.value;
  if (!root) return out;
  let idx = 0;
  function walk(node: HierarchyTreeNode): void {
    const k = node.colorKey;
    if (k !== undefined && k !== null && k !== "" && !out.has(k)) {
      out.set(k, PALETTE[idx % PALETTE.length]!);
      idx += 1;
    }
    if (node.children) for (const c of node.children) walk(c);
  }
  walk(root);
  return out;
});

// Augment the pivoted tree with per-node `itemStyle.color` /
// `label.formatter` baked in. ECharts can resolve colour via its
// `levels[]` for the depth mode; for column-mode the colour must
// ride on the node itself.
type EChartsTreeNode = HierarchyTreeNode & {
  itemStyle?: { color?: string };
  label?: { formatter?: string };
  children?: EChartsTreeNode[];
};

const seriesRoot = computed<EChartsTreeNode | null>(() => {
  const root = truncatedRoot.value;
  if (!root) return null;
  const mode = props.spec.colorBy;
  const colourMap = colorByMap.value;
  function decorate(node: HierarchyTreeNode): EChartsTreeNode {
    const out: EChartsTreeNode = {
      name: node.name,
      value: node.value,
      id: node.id,
      colorKey: node.colorKey,
    };
    if (node.children) {
      out.children = node.children.map(decorate);
    }
    if (mode && mode !== "depth") {
      const k = node.colorKey;
      const colour =
        k !== undefined && k !== null && k !== ""
          ? colourMap?.get(k)
          : undefined;
      if (colour) out.itemStyle = { color: colour };
    }
    return out;
  }
  return decorate(root);
});

// Diagnostics — cycle / orphan / depth-cap surfaces, same pattern
// as sunburst with namespaced codes so telemetry can tell the two
// chart types apart.
watch(
  pivot,
  (p) => {
    if (p.cyclicNames.length > 0) {
      emit("diagnostic", "tree_cycle_detected", {
        count: p.cyclicNames.length,
      });
    }
    if (p.orphanCount > 0) {
      emit("diagnostic", "tree_orphan_rows", { count: p.orphanCount });
    }
    if (hierarchyMaxDepth(p.roots) > TREE_MAX_DEPTH) {
      emit("diagnostic", "tree_depth_exceeded", { cap: TREE_MAX_DEPTH });
    }
  },
  { immediate: true },
);

// Multi-root → chart-error event. Fires whenever the pivot resolves
// to ≥2 roots; embedders surface this as a `lucent-error` DOM event
// alongside the inline message rendered below.
watch(
  multiRootError,
  (msg) => {
    if (!msg) return;
    emit("chart-error", {
      code: "tree_multiple_roots",
      message: msg,
      chartType: "tree",
      targetId: (props.spec as { id?: string }).id,
    });
  },
  { immediate: true },
);

// Label position derived from orient — ECharts doesn't pick a sane
// default here. For LR (roots on the left, growing rightward) we
// want labels to the *right* of each node so the label sits at the
// growth edge; leaves get labels on their right so the tree reads
// top-down by branch. For TB (roots on top) the symmetrical
// positions are top and bottom.
const labelPosition = computed(() =>
  orient.value === "LR" ? "right" : "bottom",
);
const leafLabelPosition = computed(() =>
  orient.value === "LR" ? "right" : "bottom",
);

// Label formatter — renders the display name and, when
// `valueLabel === "node"`, the formatted value. ECharts' label
// formatter callback fires per node; the closure captures the
// current valueFormat / valueLabelMode reactives via the computed
// `option` below.
const labelFormatter = computed<((params: {
  data?: { name?: string; value?: number | string };
}) => string) | undefined>(() => {
  if (valueLabelMode.value !== "node") return undefined;
  const fmt = valueFormat.value;
  return (params) => {
    const name = params.data?.name ?? "";
    const v = params.data?.value;
    if (v === undefined || v === null) return name;
    const formatted = typeof v === "number" ? formatValue(v, fmt) : String(v);
    return `${name}  ${formatted}`;
  };
});

const tooltipFormatter = computed<((params: {
  data?: { name?: string; value?: number | string };
}) => string) | undefined>(() => {
  if (valueLabelMode.value === "off") {
    // Quiet tooltip — just the name. Without this, ECharts' default
    // formatter prints "name: value" which leaks the value the
    // author asked us to hide.
    return (params) => params.data?.name ?? "";
  }
  if (valueLabelMode.value === "tooltip") {
    const fmt = valueFormat.value;
    return (params) => {
      const name = params.data?.name ?? "";
      const v = params.data?.value;
      if (v === undefined || v === null) return name;
      const formatted =
        typeof v === "number" ? formatValue(v, fmt) : String(v);
      return `${name}: ${formatted}`;
    };
  }
  // `node` mode — value already shown on the label; tooltip stays
  // quiet to avoid duplicating the same number in two places.
  return (params) => params.data?.name ?? "";
});

const option = computed(() => {
  const root = seriesRoot.value;
  if (!root) return null;
  // Depth-mode colouring uses ECharts' built-in `levels[]`, same
  // pattern as sunburst. Each entry cycles through PALETTE so depth
  // colours land on the same hue across both charts when an author
  // ports a hierarchy between sunburst and tree.
  const depthLevels =
    props.spec.colorBy === "depth"
      ? Array.from({ length: TREE_MAX_DEPTH }, (_, i) => ({
          itemStyle: { color: PALETTE[i % PALETTE.length] },
        }))
      : undefined;
  // Single-accent default — embedder token first, hex fallback.
  const accentNode =
    resolveCssVar("var(--lucent-tree-node)") ?? CHART_COLORS.treeNode;
  const edgeColor =
    resolveCssVar("var(--lucent-tree-edge)") ?? CHART_COLORS.treeEdge;
  // Margins and label widths from the chart width: ECharts' default
  // margins leave leaf labels running off the right edge of a narrow LR
  // tree. Leaves get a capped column on the growth side; inner labels
  // truncate so they don't run into the next level. Unmeasured keeps
  // ECharts' defaults.
  const w = ctx.value.width;
  const leafCap = clampPx(w * 0.3, 60, 160);
  const sized =
    w > 0
      ? {
          margins:
            orient.value === "LR"
              ? { left: clampPx(w * 0.12, 24, 80), right: leafCap + 12 }
              : { bottom: leafCap + 12 },
          leafLabel: { width: leafCap, overflow: "truncate" as const },
          innerLabel: { width: clampPx(w * 0.18, 48, 120), overflow: "truncate" as const },
        }
      : { margins: {}, leafLabel: {}, innerLabel: {} };
  return {
    ...chartChrome(ctx.value),
    tooltip: {
      ...tooltipDefaults("item", ctx.value),
      formatter: tooltipFormatter.value,
    },
    series: [
      {
        type: "tree" as const,
        data: [root],
        layout: "orthogonal" as const,
        orient: orient.value,
        initialTreeDepth:
          props.spec.initialDepth === undefined ? 1 : props.spec.initialDepth,
        ...sized.margins,
        // `roam` enables pan/zoom on the canvas. Mouse wheel zooms,
        // click-drag pans, double-click resets the view. Persona-review
        // GM pushback noted this can trap page scrolling on long
        // dashboards; the locked decision is to keep it for mice (wheel
        // events only consume when the cursor is over the canvas). On
        // touch screens a one-finger drag over the canvas WOULD capture
        // the page scroll, so roam is off there — expand / collapse and
        // the truncated labels cover navigation — and off in print.
        roam: !ctx.value.print && !ctx.value.coarse,
        expandAndCollapse: true,
        symbol: "circle",
        symbolSize: 8,
        // Default node fill — overridden per-node when colourMap
        // hands back a palette entry for that node's column value,
        // or by `levels[]` when colorBy === "depth".
        itemStyle: { color: accentNode },
        lineStyle: { color: edgeColor, width: 1 },
        label: {
          fontFamily: FONT_STACK_MONO,
          fontSize: 11,
          position: labelPosition.value,
          formatter: labelFormatter.value,
          ...sized.innerLabel,
        },
        leaves: {
          label: {
            position: leafLabelPosition.value,
            formatter: labelFormatter.value,
            ...sized.leafLabel,
          },
        },
        // Apply depth-mode levels when set. Each `levels[i]` styles
        // the node at depth i+1; index 0 styles the synthetic root,
        // 1 the first level, etc.
        ...(depthLevels ? { levels: [{}, ...depthLevels] } : {}),
        // Animation off in print mode so the rendered PDF captures
        // the final layout (matches the chartChrome convention used
        // across the other charts).
        animation: !ctx.value.print,
      },
    ],
  };
});

// Height clamp [200, 800] — wider than sunburst's [200, 400] because
// trees grow with node count and a 400px ceiling forces pan-down on
// any moderate-sized tree.
const height = computed(() =>
  resolveChartHeight("tree", props.spec.height),
);

// Click handler. Same payload shape as sunburst: `{ path, name,
// value }` — `path` is the root-to-clicked-node sequence of display
// names (skipping the synthetic root if ECharts adds one, which it
// doesn't here since we render a real root).
function onClick(p: { data?: unknown; treeAncestors?: unknown }) {
  if (!props.spec.id || !p?.data) return;
  const datum = p.data as { name?: string; value?: number };
  // ECharts tree exposes ancestors via `treeAncestors` on the click
  // params — an array from root down to the clicked node.
  const ancestors = (p.treeAncestors ?? []) as ReadonlyArray<{ name?: string }>;
  const path = ancestors
    .map((n) => n.name ?? "")
    .filter((s): s is string => s.length > 0);
  emit("click-target", props.spec.id, {
    path,
    name: datum.name ?? "",
    value: datum.value ?? 0,
  });
}
</script>

<template>
  <div
    v-if="multiRootError"
    class="chart chart-placeholder chart-placeholder--error"
    :style="{ height: `${height}px` }"
    role="alert"
  >
    {{ multiRootError }}
  </div>
  <v-chart
    ref="chartRef"
    v-else-if="option"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    :init-options="{ renderer: 'canvas' }"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
/* Multi-root inline error reuses the existing chart-error chrome
   shape from InsightChart.vue so the rejection reads as a normal
   chart error rather than a one-off layout. The colours fall back
   to embedder tokens with the same hex defaults the parent uses. */
.chart-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
  border: 1px solid var(--rule, #e5e3dc);
  border-radius: 10px;
  background: var(--card, #fbfaf6);
  color: var(--card-muted, #76767e);
  font-size: 13px;
  line-height: 1.4;
}
.chart-placeholder--error {
  color: var(--color-negative, #b34646);
}
</style>
